import { DialogueConfirmation } from '@/components/dialogue/dialogue-confirmation';
import { IconeHistorique } from '@/components/historique/icone-historique';
import { PastilleCodeNoyau } from '@/components/noyaux/pastille-code-noyau';
import { VoirAussiNoyaux } from '@/components/noyaux/voir-aussi-noyaux';
import { IconeTrait } from '@/components/ui/icone-trait';
import { useTitrePage } from '@/lib/titre-page';
import { Alert, Button, Icon, buttonVariants } from '@pms-digital/design-system';
import { NOYAUX, type Traduction } from '@rosetta/shared';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { EncadreTraduction } from './encadre-traduction';
import { EtapesAttente } from './etapes-attente';
import { formaterDepuis } from './formater-duree';
import { MessageTahiti } from './message-tahiti';

interface EcranAttenteProps {
  traduction: Traduction;
  /** Instant (ms) of the last read: the time elapsed is worded again at each read, not each second. */
  luLe: number;
  /** An interruption of this translation is running: "interrompre" is locked. */
  interruptionEnCours: boolean;
  /** The user confirmed W2: stop the translation. */
  onInterrompre: () => void;
  /** A blocked translation (#13): opens the input screen filled with it (the flow of #5). */
  onRelancer: () => void;
}

const FIN_DE_PHRASE = "Le résultat s'affichera ici dès que la traduction sera terminée.";

/** What the alert of a blocked translation says (#13). */
export const MESSAGE_BLOQUEE =
  "Une étape n'a pas pu aboutir : la traduction ne se terminera pas d'elle-même.";
export const CONSEIL_BLOQUEE =
  'Interrompez-la pour arrêter ce qui tourne encore, puis relancez-la si vous le souhaitez.';

/** When it began, when Tahiti says so ("à l'instant" for the first seconds, #13). */
function depuis(commenceeLe: string | undefined, luLe: number): string | null {
  if (commenceeLe === undefined) return null;
  const ecoule = (luLe - Date.parse(commenceeLe)) / 1000;
  return Number.isFinite(ecoule) ? `Commencée ${formaterDepuis(ecoule)}.` : null;
}

/**
 * The waiting screen W1 (#7), at the address of the result while the translation runs: the
 * title, "voir l'historique" and "interrompre", a box with the title, three lines of the
 * original text and the noyaux aimed at, then the steps and the time elapsed. "interrompre"
 * asks for a confirmation (W2) before anything is cancelled. The screen is read every 5 s (the
 * parent's query): the result opens at the same address when it is done. Under the title, "Voir
 * aussi :" (#36) lists the noyaux of the document that other launches already translated, each a
 * link to its result; nothing when there is none.
 *
 * A blocked translation (#13: one of its steps failed, Tahiti will not end it) is the same screen
 * in its "anomalie" state: "Traduction bloquée", an alert that says so (with the message of
 * Tahiti when it is readable), the failed step marked in the list, "relancer" next to
 * "interrompre", and no more reading (the parent's query stops).
 */
export function EcranAttente({
  traduction,
  luLe,
  interruptionEnCours,
  onInterrompre,
  onRelancer,
}: EcranAttenteProps) {
  const bloquee = traduction.statut === 'bloquee';
  useTitrePage(bloquee ? 'Traduction bloquée' : 'Traduction en cours');
  const [confirmation, setConfirmation] = useState(false);
  const { etapes, commenceeLe, messageErreur, autresTraductions = [] } = traduction;
  const debut = depuis(commenceeLe, luLe);

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-h2 leading-[1.2]">
          <span className="font-light">Traduction</span>{' '}
          <span className="font-bold">{bloquee ? 'bloquée' : 'en cours'}</span>
        </h1>
        <div className="flex flex-wrap gap-2">
          <Link to="/historique" className={`${buttonVariants({ variant: 'ghost' })} no-underline`}>
            <Icon name="history" size={20} />
            voir l'historique
          </Link>
          {bloquee ? (
            <Button type="button" variant="primary" onClick={onRelancer}>
              <IconeHistorique name="refresh" size={20} />
              relancer
            </Button>
          ) : null}
          <Button
            type="button"
            variant="primary"
            aria-disabled={interruptionEnCours}
            aria-busy={interruptionEnCours}
            onClick={interruptionEnCours ? undefined : () => setConfirmation(true)}
          >
            <IconeTrait name="stop_circle" size={20} />
            {interruptionEnCours ? 'interruption...' : 'interrompre'}
          </Button>
        </div>
      </div>

      <VoirAussiNoyaux
        className="mt-3"
        libelle="Voir aussi :"
        idTitre="voir-aussi-titre"
        noyaux={autresTraductions.map((ligne) => ligne.noyau)}
        lien={(code) => (
          <Link
            to="/resultat/$id"
            params={{
              id: autresTraductions.find((ligne) => ligne.noyau === code)?.lancement ?? '',
            }}
            search={{ noyau: code }}
            aria-label={`résultat de la traduction vers ${code}, ${NOYAUX[code].famille} ${NOYAUX[code].variante}`}
            className="inline-flex h-6 items-center rounded-full px-1 no-underline hover:bg-gray-100"
          >
            <PastilleCodeNoyau noyau={code} />
          </Link>
        )}
      />

      {bloquee ? (
        <div className="mt-8">
          <Alert tone="danger" title={MESSAGE_BLOQUEE}>
            <p className="text-gray-700 text-small leading-[1.5]">{CONSEIL_BLOQUEE}</p>
            {messageErreur === undefined ? null : <MessageTahiti message={messageErreur} />}
          </Alert>
        </div>
      ) : null}

      <EncadreTraduction traduction={traduction} />

      <EtapesAttente etapes={etapes} />

      {bloquee ? (
        debut === null ? null : (
          <p className="mt-6 text-gray-700 text-small leading-normal">{debut}</p>
        )
      ) : (
        <output className="mt-6 block text-gray-700 text-small leading-normal">
          {debut === null ? FIN_DE_PHRASE : `${debut} ${FIN_DE_PHRASE}`}
        </output>
      )}

      <DialogueConfirmation
        open={confirmation && !interruptionEnCours}
        title="Interrompre cette traduction ?"
        description="Le traitement s'arrête et ne pourra pas reprendre. Vous pourrez relancer la traduction depuis l'historique."
        cancelLabel="annuler"
        confirmLabel="interrompre"
        confirmDanger
        onCancel={() => setConfirmation(false)}
        onConfirm={() => {
          setConfirmation(false);
          onInterrompre();
        }}
      />
    </>
  );
}
